import React from "react"
import { useTranslation } from "react-i18next"

import {
	consumers,
	checkIsPrivateConsumer,
	checkIsCompanyConsumer,
	PRIVATE_GENDER_VALUE,
} from "utils/constants"
import { UserOnSignup, ErrorResponse } from "models"

import { useForm, useAlert, useOperator } from "hooks"
import { useSignupProcess, SignupActionType } from "../../hooks/useSignupProcess"

import * as AuthApi from "features/auth/api/AuthApi"

import { Button } from "components/Elements"
import { formStatusEnum, FormElement } from "components/Form"

type SignupFormProps = Record<string, never>

const defaultCountry = "France"

const defaultFormFields: UserOnSignup = {
	password: "",
	code: "",
	operator: "",
	description: {
		gender: PRIVATE_GENDER_VALUE.toString(),
		social_reason: "",
		last_name: "",
		first_name: "",
		phone: "",
		mail: "",
		address: {
			street_number: "",
			street_name: "",
			postal_code: "",
			insee_code: "",
			city: "",
			country: defaultCountry,
		},
	},
	confirmMail: "",
	cgu_version: "",
	operator_notifications_accepted: "",
	ocode_notifications_accepted: "",
}

export const SignupForm: React.FC<SignupFormProps> = () => {
	const { signupDispatch, signupState } = useSignupProcess()
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		showGlobalError,
		handleValidation,
	} = useForm({
		defaultFormFields: signupState?.data ?? defaultFormFields,
		defaultFormStatus: formStatusEnum.ready,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentOperator, isBicycode } = useOperator()

	React.useEffect(() => {
		// Operator is required for register a new account
		setFormFields((formFields: UserOnSignup) => ({
			...formFields,
			operator: currentOperator.id,
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOperator])

	const isLoading = formStatus === formStatusEnum.loading

	const consumersOptions = consumers.map((consumer) => ({
		...consumer,
		value: consumer.value.toString(),
	}))

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const { getError } = await AuthApi.signUpCheckMail(
					formFields.description.mail,
					currentOperator.id
				)

				if (getError()) throw getError()

				// save signup data and go to next step
				signupDispatch({
					type: SignupActionType.VALIDATE,
					payload: formFields,
				})
			} catch (error) {
				// console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const isPrivateUser = checkIsPrivateConsumer({
		gender: formFields.description.gender,
	})
	const isCompany = checkIsCompanyConsumer({
		gender: formFields.description.gender,
	})

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form className="flex flex-col" autoComplete="off" id="formSignUp">
			{showGlobalError && (
				<p className="u-fs-xs u-danger u-mg-bottom-m">
					{t("signin_global_error", { ns: "auth" })}
				</p>
			)}
			<h2 className="font-bold text-4xl py-4 text-center">
				{t("signup.creation_title", { ns: "auth" })}
			</h2>

			<h3 className="font-bold text-center mb-4 block text-primary max-w-sm mx-auto">
				{t("signup.introduction", {
					ns: "auth",
				})}
			</h3>

			<FormElement
				{...formElementCommonProps}
				value={formFields.description.gender}
				name="description.gender"
				type="radio"
				options={consumersOptions}
				classNameOptionsContainer="flex"
				classNameOption="ml-2"
				className="mb-3"
				required={true}
			/>
			{isPrivateUser && (
				<div className="flex justify-between flex-wrap mb-1">
					<FormElement
						{...formElementCommonProps}
						value={formFields.description.last_name}
						name="description.last_name"
						id="last_name"
						type="text"
						label={t("lastname", { ns: "user" })}
						className="w-full sm:w-2/4 px-1"
						required={true}
					/>

					<FormElement
						{...formElementCommonProps}
						value={formFields.description.first_name}
						name="description.first_name"
						id="first_name"
						type="text"
						label={t("firstname", { ns: "user" })}
						className="w-full sm:w-2/4 px-1"
						required={true}
					/>
				</div>
			)}
			{isCompany && (
				<>
					<FormElement
						{...formElementCommonProps}
						value={formFields.description.social_reason}
						name="description.social_reason"
						id="social_reason"
						type="text"
						label={t("raison_sociale", { ns: "user" })}
						className="px-1 pb-1"
						required={true}
					/>
				</>
			)}
			<FormElement
				{...formElementCommonProps}
				value={formFields.description.phone}
				name="description.phone"
				id="phone"
				type="phone"
				label={t("phone", { ns: "user" })}
				className="px-1"
				required={true}
			/>
			<FormElement
				{...formElementCommonProps}
				value={formFields.description.mail}
				name="description.mail"
				type="email"
				id="mail"
				label={t("email", { ns: "user" })}
				className="mt-1 mb-3"
				required={true}
			/>
			<FormElement
				{...formElementCommonProps}
				value={formFields.confirmMail}
				mailToConfirm={formFields.description.mail}
				name="confirmMail"
				type="email"
				id="confirmMail"
				label={t("confirm_email", { ns: "user" })}
				className="my-1"
				required={true}
			/>
			<FormElement
				{...formElementCommonProps}
				value={formFields.password}
				name="password"
				type="password"
				id="password"
				label={t("password", { ns: "user" })}
				className="my-1"
				required={true}
				showPasswordRule={true}
				autocomplete="new-password"
			/>
			{/* cgu */}
			<FormElement
				{...formElementCommonProps}
				value={formFields.cgu_version}
				name="cgu_version"
				type="checkbox"
				label=""
				required={true}
				checkboxLabel={
					<>
						{t("checkbox_cgu_begin", { ns: "auth" })}
						<a
							href={currentOperator.cguUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="px-1 underline"
						>
							{t("checkbox_cgu_link", { ns: "auth" })}
						</a>
						{t("checkbox_cgu_end", { ns: "auth" })}
					</>
				}
				className="text-xxs"
			/>

			{/* newsletter */}
			{isBicycode && (
				<FormElement
					{...formElementCommonProps}
					value={formFields.operator_notifications_accepted}
					name="operator_notifications_accepted"
					type="checkbox"
					label=""
					checkboxLabel={t("label_newsletter", {
						ns: `auth_${currentOperator.folderName}`,
					})}
					className="text-xxs"
				/>
			)}

			<div className="flex justify-center mt-10">
				<Button
					className={"px-4"}
					variant="secondary"
					isLoading={isLoading}
					type="submit"
					onClick={handleSubmit}
				>
					{t("global.validate")}
				</Button>
			</div>
			{/* {process.env.REACT_APP_MODE !== "production" && (
				<div className="flex justify-center py-4">
					<button
						type="button"
						onClick={() =>
							setFormFields(
								// prettier-ignore
								generateUserSignup({
									"password": process.env.REACT_APP_FILL_SIGNUP_PASSWORD || "",
									"description.mail": process.env.REACT_APP_FILL_SIGNUP_EMAIL || "",
									"confirmPassword": process.env.REACT_APP_FILL_SIGNUP_PASSWORD || "",
									"confirmMail": process.env.REACT_APP_FILL_SIGNUP_EMAIL || "",
									"description.phone": "+33333333333"
								})
							)
						}
					>
						Fill form
					</button>
				</div>
			)} */}
		</form>
	)
}
